import { 
  Smartphone, 
  Zap, 
  TrendingUp, 
  Layers, 
  Shield, 
  Users,
  Brain,
  Code,
  Cpu,
  Cloud,
  Target,
  Palette,
  CheckCircle,
  Database,
  Lock,
  Eye,
  AlertTriangle,
  Network,
  Settings
} from 'lucide-react';
import type { ProductConfigs } from './types';

export const mobileDevelopmentSolutionsConfigs: ProductConfigs = {
  'ios-app-development-suite': {
    keyBenefits: [
      {
        icon: TrendingUp,
        title: 'Native iOS Performance & Optimization',
        description: 'Swift-optimized code architecture delivering 60fps performance with minimal battery drain and memory usage.',
        metrics: '60fps performance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'App Store Revenue Acceleration',
        description: 'Strategic App Store optimization and monetization strategies that increase revenue by 300% within 6 months.',
        metrics: '300% revenue growth',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Layers,
        title: 'Apple Ecosystem Integration Excellence',
        description: 'Seamless integration with iCloud, Apple Pay, Siri, and all Apple services for maximum user convenience.',
        metrics: 'Complete Apple integration',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Palette,
        title: 'SwiftUI Modern Interface Development',
        description: 'Next-generation user interfaces built with SwiftUI that reduce development time by 40% while enhancing UX.',
        metrics: '40% faster development',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Shield,
        title: 'iOS Security & Privacy Compliance',
        description: 'Built-in privacy protection, App Tracking Transparency compliance, and secure data handling meeting Apple standards.',
        metrics: 'Apple compliance',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'App Store Optimization & Marketing',
        description: 'Expert ASO strategies including keyword optimization, screenshot design, and conversion rate optimization.',
        metrics: 'ASO excellence',
        color: 'from-indigo-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered iOS Development Tools',
        description: 'Machine learning integration with Core ML and Create ML for intelligent app features and user experiences.',
        metrics: 'AI-powered features',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Code,
        title: 'Core ML & Create ML Integration',
        description: 'Advanced machine learning model integration for on-device AI processing with privacy-first architecture.',
        metrics: 'On-device AI',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Cpu,
        title: 'ARKit & RealityKit Implementation',
        description: 'Cutting-edge augmented reality experiences using Apple\'s latest AR technologies and frameworks.',
        metrics: 'AR experiences',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Cloud,
        title: 'CloudKit & Core Data Synchronization',
        description: 'Seamless data synchronization across all user devices with CloudKit integration and offline capabilities.',
        metrics: 'Cross-device sync',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Target,
        title: 'Advanced Push Notification System',
        description: 'Rich media notifications, interactive notifications, and personalized messaging with analytics tracking.',
        metrics: 'Rich notifications',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Settings,
        title: 'Siri Shortcuts & App Intents',
        description: 'Voice-activated app functionality with Siri integration and custom shortcuts for enhanced user productivity.',
        metrics: 'Siri integration',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: CheckCircle,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR, and Apple App Store guidelines compliance with automated audit trails',
        metrics: 'Multi-standard compliance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring, threat detection, and performance analytics with predictive insights',
        metrics: 'Real-time monitoring',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Cloud,
        title: 'Scalable Security Infrastructure',
        description: 'Auto-scaling security architecture that grows with your user base while maintaining consistent protection',
        metrics: 'Auto-scaling security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Enterprise-grade firewall protection with intelligent DDoS mitigation and traffic filtering systems',
        metrics: 'Enterprise firewall',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA implementation with biometric authentication, hardware tokens, and adaptive security',
        metrics: 'Biometric MFA',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Advanced SIEM capabilities with AI-powered threat detection, incident response, and forensic analysis',
        metrics: 'AI threat detection',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, real-time loss prevention, encrypted backup systems, and instant recovery protocols',
        metrics: 'Zero data loss guarantee',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation, network anomaly detection, and continuous traffic analysis with AI-powered insights',
        metrics: 'Complete network visibility',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing simulation, and continuous compliance validation with reporting',
        metrics: 'Proactive security posture',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  },

  'android-app-development-suite': {
    keyBenefits: [
      {
        icon: TrendingUp,
        title: 'Native Android Performance Excellence',
        description: 'Kotlin-optimized architecture delivering exceptional performance across all Android devices and versions.',
        metrics: 'Peak performance',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Zap,
        title: 'Google Play Revenue Optimization',
        description: 'Advanced Play Store optimization and monetization strategies that boost revenue by 350% within 6 months.',
        metrics: '350% growth',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: Palette,
        title: 'Material Design Implementation',
        description: 'Beautiful Material Design 3 interfaces that enhance user experience and app store ratings by 40%.',
        metrics: '40% rating improvement',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Cloud,
        title: 'Google Services Integration Suite',
        description: 'Complete integration with Google Play Services, Firebase, and Google Cloud for maximum functionality.',
        metrics: 'Full Google integration',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Shield,
        title: 'Android Security Framework',
        description: 'Advanced security implementation with Android Keystore, biometric authentication, and data encryption.',
        metrics: 'Enterprise security',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Play Store Optimization & ASO',
        description: 'Expert App Store Optimization with keyword research, listing optimization, and conversion improvement.',
        metrics: 'ASO excellence',
        color: 'from-indigo-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Android Development',
        description: 'TensorFlow Lite and ML Kit integration for on-device machine learning and intelligent app features.',
        metrics: 'AI-powered features',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Code,
        title: 'TensorFlow Lite & ML Kit Integration',
        description: 'Advanced machine learning capabilities with on-device processing for privacy-first AI experiences.',
        metrics: 'On-device ML',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Layers,
        title: 'Jetpack Compose Modern UI',
        description: 'Next-generation UI toolkit that reduces development time by 50% while creating stunning interfaces.',
        metrics: '50% faster UI development',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Cloud,
        title: 'Firebase & Google Cloud Integration',
        description: 'Comprehensive backend services with real-time databases, authentication, and cloud functions.',
        metrics: 'Full-stack integration',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Cpu,
        title: 'Advanced Android Architecture Components',
        description: 'Modern MVVM architecture with LiveData, ViewModel, and Room for maintainable, scalable apps.',
        metrics: 'Modern architecture',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Settings,
        title: 'Google Assistant & Actions Integration',
        description: 'Voice-activated functionality with Google Assistant integration and custom actions for enhanced UX.',
        metrics: 'Voice integration',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: CheckCircle,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR, and Google Play Store policy compliance with automated audit trails',
        metrics: 'Multi-standard compliance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring, threat detection, and performance analytics with predictive insights',
        metrics: 'Real-time monitoring',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Cloud,
        title: 'Scalable Security Infrastructure',
        description: 'Auto-scaling security architecture that grows with your user base while maintaining consistent protection',
        metrics: 'Auto-scaling security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Enterprise-grade firewall protection with intelligent DDoS mitigation and traffic filtering systems',
        metrics: 'Enterprise firewall',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA implementation with biometric authentication, hardware tokens, and adaptive security',
        metrics: 'Biometric MFA',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Advanced SIEM capabilities with AI-powered threat detection, incident response, and forensic analysis',
        metrics: 'AI threat detection',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, real-time loss prevention, encrypted backup systems, and instant recovery protocols',
        metrics: 'Zero data loss guarantee',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation, network anomaly detection, and continuous traffic analysis with AI-powered insights',
        metrics: 'Complete network visibility',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing simulation, and continuous compliance validation with reporting',
        metrics: 'Proactive security posture',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  }
};